import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { HeroBadge } from '../sections/HeroBadge.jsx';
import {
    CAPTERRA_REVIEWS_URL,
    G2_REVIEWS_URL,
    GETAPP_REVIEWS_URL,
    SOFTWARE_ADVICE_REVIEWS_URL,
    SOURCEFORGE_REVIEWS_URL,
} from '../../utils/pokJsonLd.js';
import { INK, FG_2, FG_3, LINE, PURPLE, YELLOW, SURFACE, gridBg } from '../sections/theme.js';

const PLATFORMS = [
    { name: 'G2', rating: '5.0', reviews: 38, href: G2_REVIEWS_URL },
    { name: 'Capterra', rating: '5.0', reviews: 9, href: CAPTERRA_REVIEWS_URL },
    { name: 'GetApp', rating: '5.0', reviews: 9, href: GETAPP_REVIEWS_URL },
    { name: 'Software Advice', rating: '5.0', reviews: 9, href: SOFTWARE_ADVICE_REVIEWS_URL },
];

export default function PlatformsIn() {
    const { t } = useTranslation();

    return (
        <Box
            component="section"
            id="resenas"
            sx={{ ...gridBg(SURFACE.lavender), borderTop: '1px solid rgba(26,23,38,0.06)', py: { xs: 7, md: 9 } }}
        >
            <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', maxWidth: 680, mx: 'auto', mb: 5 }}>
                    <HeroBadge label={t('home.platforms_tag')} />
                    <Typography
                        component="h2"
                        sx={{
                            fontSize: { xs: '1.75rem', md: '2.125rem' },
                            fontWeight: 700,
                            lineHeight: 1.2,
                            letterSpacing: '-0.01em',
                            color: INK,
                            mb: 1.5,
                        }}
                    >
                        {t('home.platforms_title')}
                    </Typography>
                    <Typography sx={{ fontSize: '1.0625rem', fontWeight: 400, color: FG_2, lineHeight: 1.55 }}>
                        {t('home.platforms_subtitle')}
                    </Typography>
                </Box>

                <Grid
                    container
                    spacing={2.5}
                    sx={{
                        justifyContent: 'center',
                    }}
                >
                    {PLATFORMS.map((p) => (
                        <Grid key={p.name} size={{ xs: 6, md: 3 }}>
                            <Box
                                component="a"
                                href={p.href}
                                target="_blank"
                                rel="nofollow noopener noreferrer"
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: 1,
                                    height: '100%',
                                    background: '#fff',
                                    border: `1px solid ${LINE}`,
                                    borderRadius: '20px',
                                    px: 2.5,
                                    py: 3.25,
                                    textDecoration: 'none',
                                    boxShadow: '0 1px 2px rgba(20,4,77,0.08)',
                                    transition: 'box-shadow 0.2s, transform 0.2s',
                                    '&:hover': {
                                        boxShadow: '0 16px 36px -18px rgba(20,11,61,0.25)',
                                        transform: 'translateY(-4px)',
                                    },
                                }}
                            >
                                <Typography sx={{ fontSize: '1.125rem', fontWeight: 700, color: INK }}>
                                    {p.name}
                                </Typography>
                                <Box
                                    component="span"
                                    aria-label="5 / 5"
                                    sx={{ color: YELLOW, fontSize: '1.125rem', letterSpacing: '2px' }}
                                >
                                    ★★★★★
                                </Box>
                                <Typography
                                    sx={{ fontSize: '1.625rem', fontWeight: 700, lineHeight: 1, color: PURPLE }}
                                >
                                    {p.rating}
                                </Typography>
                                <Typography sx={{ fontSize: '0.8rem', fontWeight: 400, color: FG_3 }}>
                                    {p.reviews} {t('home.platforms_reviews')}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>

                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Box
                        component="a"
                        href={SOURCEFORGE_REVIEWS_URL}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            background: '#fff',
                            border: `1px solid ${LINE}`,
                            borderRadius: '999px',
                            px: 2.25,
                            py: 1,
                            fontSize: '0.9375rem',
                            fontWeight: 600,
                            color: INK,
                            textDecoration: 'none',
                            boxShadow: '0 1px 2px rgba(20,4,77,0.08)',
                            transition: 'box-shadow 0.2s',
                            '&:hover': { boxShadow: '0 10px 24px -14px rgba(20,11,61,0.3)' },
                        }}
                    >
                        <Box component="span" aria-hidden="true" sx={{ color: YELLOW }}>
                            ★
                        </Box>
                        {t('home.platforms_award')}
                    </Box>
                </Box>
            </Container>
        </Box>
    );
}
